import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { gigCategories, gigSubcategories } from '@/mocks/gigs';

export interface GigRecord {
  id?: string;
  owner_id?: string;
  title: string;
  freelancer: string;
  category: string;
  subcategory: string;
  price: number | null;
  delivery_days: number | null;
  location: string;
  description: string;
  deliverables: string;
}

const locations = ['À distance', 'Lomé', 'Sokodé', 'Kara', 'Atakpamé', 'Kpalimé', 'Tsévié', 'Dapaong'];

interface GigFormProps {
  ownerId: string;
  existing?: GigRecord | null;
  onSaved: () => void;
}

function emptyForm(): GigRecord {
  return {
    title: '',
    freelancer: '',
    category: gigCategories[0],
    subcategory: '',
    price: null,
    delivery_days: null,
    location: 'À distance',
    description: '',
    deliverables: '',
  };
}

function GigForm({ ownerId, existing, onSaved }: GigFormProps) {
  const [form, setForm] = useState<GigRecord>(() =>
    existing ? { ...existing, subcategory: existing.subcategory ?? '' } : emptyForm(),
  );
  const [subChoice, setSubChoice] = useState(() => {
    const sub = existing?.subcategory;
    if (!sub) return '';
    const list = gigSubcategories[existing.category] || [];
    return list.includes(sub) ? sub : '__other__';
  });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const update = (field: keyof GigRecord, value: string | number | null) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setStatus('idle');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.freelancer.trim()) {
      setStatus('error');
      setErrorMsg('Le titre du service et votre nom sont obligatoires.');
      return;
    }

    setSaving(true);
    setStatus('idle');
    setErrorMsg('');

    const payload = {
      title: form.title.trim(),
      freelancer: form.freelancer.trim(),
      category: form.category,
      subcategory: form.subcategory.trim() || null,
      price: form.price !== null && form.price > 0 ? form.price : null,
      delivery_days: form.delivery_days !== null && form.delivery_days > 0 ? form.delivery_days : null,
      location: form.location.trim() || null,
      description: form.description.trim() || null,
      deliverables: form.deliverables.trim() || null,
    };

    let error = null;
    if (existing?.id) {
      const res = await supabase.from('gigs').update(payload).eq('id', existing.id);
      error = res.error;
    } else {
      const res = await supabase.from('gigs').insert({ ...payload, owner_id: ownerId });
      error = res.error;
    }

    setSaving(false);

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }

    onSaved();
  };

  const subs = gigSubcategories[form.category] || [];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
            <i className="ri-briefcase-4-line text-accent-400" />
          </span>
          Informations du service
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Titre du service *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Ex : Je crée votre site vitrine professionnel"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-accent-400"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Votre nom (freelance) *</label>
            <input
              type="text"
              value={form.freelancer}
              onChange={(e) => update('freelancer', e.target.value)}
              placeholder="Votre nom ou votre marque"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-accent-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Catégorie</label>
            <select
              value={form.category}
              onChange={(e) => {
                const cat = e.target.value;
                setForm((prev) => ({ ...prev, category: cat, subcategory: '' }));
                setSubChoice('');
                setStatus('idle');
              }}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-accent-400"
            >
              {gigCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          {subs.length > 0 && (
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-foreground-400 mb-1.5">Domaine / spécialité</label>
              <select
                value={subChoice}
                onChange={(e) => {
                  const val = e.target.value;
                  setSubChoice(val);
                  setForm((prev) => ({ ...prev, subcategory: val === '__other__' ? '' : val }));
                  setStatus('idle');
                }}
                className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-accent-400"
              >
                <option value="">Choisir un domaine...</option>
                {subs.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
                <option value="__other__">Autre (préciser)</option>
              </select>
              {subChoice === '__other__' && (
                <input
                  type="text"
                  value={form.subcategory}
                  onChange={(e) => update('subcategory', e.target.value)}
                  placeholder="Écrivez votre domaine"
                  className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-accent-400 mt-2"
                />
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Localisation</label>
            <select
              value={form.location}
              onChange={(e) => update('location', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-accent-400"
            >
              {locations.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Prix (FCFA)</label>
            <input
              type="number"
              min="0"
              value={form.price ?? ''}
              onChange={(e) => update('price', e.target.value !== '' ? Number(e.target.value) : null)}
              placeholder="150000"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-accent-400"
            />
            <p className="text-xs text-foreground-500 mt-1">Laissez vide pour un devis sur mesure.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Délai de livraison (jours)</label>
            <input
              type="number"
              min="0"
              value={form.delivery_days ?? ''}
              onChange={(e) => update('delivery_days', e.target.value !== '' ? Number(e.target.value) : null)}
              placeholder="7"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-accent-400"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">Description du service</label>
          <textarea
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            rows={5}
            maxLength={2000}
            placeholder="Décrivez ce que vous proposez, le public visé et les bénéfices..."
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-accent-400 resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">
            Ce qui est inclus (livrables)
          </label>
          <textarea
            value={form.deliverables}
            onChange={(e) => update('deliverables', e.target.value)}
            rows={5}
            maxLength={1000}
            placeholder={'Un livrable par ligne, par exemple :\nSite vitrine complet\nDesign responsive\n1 mois de support'}
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-accent-400 resize-none"
          />
          <p className="text-xs text-foreground-500 mt-1">Chaque ligne sera affichée comme un livrable distinct.</p>
        </div>
      </section>

      {status === 'error' && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">{errorMsg}</div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2.5 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors disabled:opacity-60 whitespace-nowrap"
        >
          {saving ? 'Enregistrement...' : existing?.id ? 'Mettre à jour' : 'Publier le service'}
        </button>
      </div>
    </form>
  );
}

export default GigForm;